import { faCircleUser, faClipboard, faBell as faBellSolid } from "@fortawesome/free-regular-svg-icons";
import {
  faAngleDown,
  faClipboardCheck,
  faEnvelope,
  faKey,
  faRightFromBracket,
  faUser,
  faUserCircle,
  faBell as faBellFilled,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getAuth, signOut } from "firebase/auth";
import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";

import atportLogo from "atport_logo.svg";
import Button from "components/Button";
import Popup from "components/Popup";
import color from "constants/color";
import font from "constants/font";
import useFetchUnreadNewsCount from "hooks/api/useFetchUnreadNewsCount";
import useNotifyError from "hooks/api/useNotifyError";
import { useGoogleAnalytics } from "hooks/useGoogleAnalytics";
import useSharedModalContext from "hooks/useSharedModalContext";
import useUserContext from "hooks/useUserContext";
import { copyOnClipboard } from "utils/clipboardUtils";
// パーツ定義
const Wrap = styled.header`
  height: 60px;
  display: flex;
  align-items: center;
  flex-shrink: 0;
  background-color: ${color.white};
  box-shadow: 0 0 16px 0 rgba(0, 0, 0, 0.15);
  & > button {
    margin-left: auto;
  }
`;
const Logo = styled.img`
  width: 88px;
  padding: 16px 30px;
  border-right: solid 1px ${color.border};
  :hover {
    cursor: pointer;
  }
`;
const NavigationArea = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 32px;
  padding: 0 32px;
`;
const Link = styled.span<{ isCurrent?: boolean; budge?: boolean }>`
  position: relative;
  height: 60px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  color: ${({ isCurrent }) => (isCurrent ? color.text.black : color.text.gray)};
  box-sizing: border-box;
  cursor: pointer;
  transition: 0.3s;
  // 通知の丸
  :after {
    display: ${({ budge }) => (budge ? "block" : "none")};
    content: "";
    position: absolute;
    width: 7px;
    height: 7px;
    border-radius: 100%;
    top: 14px;
    right: -2px;
    z-index: 1;
    background-color: ${color.attention};
  }
  :before {
    position: absolute;
    content: "";
    width: 100%;
    height: 3px;
    left: 0;
    bottom: 0;
    background: ${color.text.link};
    transform: ${({ isCurrent }) => (isCurrent ? "scale(1, 1)" : "scale(1, 0)")};
    transform-origin: center bottom;
    transition: transform 0.3s;
  }
  :hover:before {
    transform: scale(1, 1);
  }
  :hover {
    color: ${color.text.black};
  }
`;
const MenuList = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 0 16px;
  margin-left: 10px;
  min-width: 160px;
  height: 100%;
  box-sizing: border-box;
  transition: 0.3s;
  :before {
    position: absolute;
    content: "";
    top: 12px;
    left: 0;
    width: 1px;
    height: 36px;
    background-color: ${color.border};
  }
  :hover {
    cursor: pointer;
    color: ${color.text.black};
    background-color: ${color.background};
  }
`;
const UserIconWrap = styled.div`
  width: 28px;
  height: 36px;
  display: flex;
  justify-content: center;
  align-items: center;
`;
const UserWrap = styled.div<{ isBlack?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ isBlack }) => (isBlack ? color.text.black : color.text.gray)};
  :hover {
    color: ${color.text.black};
  }
`;
const UserName = styled.span`
  font-size: ${font.size14};
  display: flex;
  flex-direction: column;
`;
const AngleDownIcon = styled(FontAwesomeIcon)`
  color: ${color.text.gray};
  margin-left: 10px;
`;
const SubNavigationArea = styled.div<{ hidden: boolean }>`
  position: absolute;
  width: 100%;
  top: 60px;
  left: 0;
  z-index: 2;
  background-color: ${color.white};
  display: ${({ hidden }) => (hidden ? "none" : "flex")};
  flex-direction: column;
  border-radius: 0 0 0 4px;
  box-shadow:
    0 10px 14px 0 rgba(0, 0, 0, 0.07),
    7px 7px 14px -7px rgba(0, 0, 0, 0.07);
`;
const SubLink = styled.span`
  height: 53px;
  font-size: ${font.size14};
  padding-left: 16px;
  border-top: 1px solid ${color.border};
  box-sizing: border-box;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  color: ${color.text.gray};
  cursor: pointer;
  transition: 0.3s;
  :hover {
    color: ${color.text.black};
    background-color: ${color.background};
  }
`;
const SubLinkOfContactId = styled.span`
  font-size: ${font.size14};
  border-top: 1px solid ${color.border};
  box-sizing: border-box;
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  color: ${color.text.gray};
  transition: 0.3s;
  padding: 10px;
  :hover {
    cursor: auto;
    color: ${color.text.black};
    background-color: ${color.background};
  }
`;
const ContactIdWrap = styled.div`
  padding: 8px;
  min-width: 80%;
  background: ${color.background};
  font-size: ${font.size12};
  border-radius: 4px;
  margin-bottom: 10px;
  transition: 0.3s;
  ${SubLinkOfContactId}:hover & {
    background-color: ${color.white};
  }
`;
const ContactIdLabel = styled.div`
  padding: 8px 0 8px 0;
  color: ${color.text.disabled};
  font-size: ${font.size12};
`;
const ContactIdBody = styled.div`
  width: fit-content;
  display: flex;
  font-size: ${font.size12};
  justify-content: space-between;
  width: 100%;
`;
const IconWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${color.text.link};
`;
const IconLabel = styled.div`
  font-size: ${font.size12};
`;
const PopupHelper = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;
const ButtonWrap = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: end;
  align-items: center;
  flex: 1;
  gap: 8px;
`;

const NotificationWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;

  margin-left: 10px;
  margin-right: -8px;
  padding: 0 16px;
  height: 100%;
  box-sizing: border-box;
  transition: 0.3s;

  :before {
    position: absolute;
    content: "";
    top: 12px;
    left: 0;
    width: 1px;
    height: 36px;
    background-color: ${color.border};
  }
`;

const NotificationArea = styled.div<{ isCurrent?: boolean }>`
  position: relative;
  height: 60px;
  padding: 0 8px;
  display: flex;
  align-items: center;
  color: ${({ isCurrent }) => (isCurrent ? color.text.black : color.text.gray)};
  box-sizing: border-box;
  cursor: pointer;
  transition: 0.3s;
  :hover:before {
    transform: scale(1, 1);
  }
  :hover {
    color: ${color.text.black};
  }
`;

const UnreadBadge = styled.span`
  position: absolute;
  top: 5px;
  right: -10px;
  background-color: #ee8f00;
  color: white;
  font-size: 12px;
  font-weight: bold;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const NotificationLabel = styled.span`
  margin-left: 8px;
  font-size: ${font.size14};
  transition: 0.3s;
  ${NotificationArea}:hover & {
    color: ${color.text.black};
  }
`;

const Header = () => {
  const [isOpened, setIsOpened] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { user } = useUserContext();
  const { sendEvent } = useGoogleAnalytics(true);
  const { showModal } = useSharedModalContext();
  const { unreadCount } = useFetchUnreadNewsCount();
  const { notifyError } = useNotifyError();

  const navRoot = () => navigate("/");
  const navOrderCreate = () => {
    showModal({ modalType: "OrderMethodModal" });
  };
  const navOptionCreate = () => {
    navigate("option/create");
  };
  const navOrder = () => {
    sendEvent({
      action: "検索依頼_ヘッダー",
      category: "ヘッダー",
      label: location.pathname.replace(/\/\w{18}($|\/)/, "/detail$1"),
    });
    navigate("/order");
  };
  const navPassword = () => navigate("password");
  const navMfa = () => navigate("mfa");

  const navBilling = () => {
    sendEvent({
      action: "請求一覧_ヘッダー",
      category: "ヘッダー",
      label: location.pathname.replace(/\/\w{18}($|\/)/, "/detail$1"),
    });
    navigate("billing");
  };
  const navUsage = () => {
    sendEvent({
      action: "利用状況_ヘッダー",
      category: "ヘッダー",
      label: location.pathname.replace(/\/\w{18}($|\/)/, "/detail$1"),
    });
    navigate("usage");
  };
  const navOption = () => {
    sendEvent({
      action: "各種申請_ヘッダー",
      category: "ヘッダー",
      label: location.pathname.replace(/\/\w{18}($|\/)/, "/detail$1"),
    });
    navigate("option");
  };
  const navEmailSetting = () => {
    sendEvent({
      action: "通知メール設定_ヘッダー",
      category: "ヘッダー",
      label: location.pathname.replace(/\/\w{18}($|\/)/, "/detail$1"),
    });
    navigate("email-setting");
  };

  const logout = () => {
    const auth = getAuth();
    signOut(auth).catch((e) => {
      notifyError({ reason: "signOut", error: e, category: "firebase.auth" });
      throw e;
    });
    navigate("login");
  };

  const [copied, setCopied] = useState(false);

  return (
    <Wrap>
      <Logo src={atportLogo} alt="at PORT" onClick={navRoot} />
      <NavigationArea>
        <Link onClick={navRoot} isCurrent={"/" === location.pathname}>
          ホーム
        </Link>
        <Link onClick={navOrder} isCurrent={/^\/order\/*/.test(location.pathname)}>
          駐車場検索
        </Link>
        <Link onClick={navBilling} isCurrent={/^\/billing\/*/.test(location.pathname)}>
          請求一覧
        </Link>
        <Link onClick={navUsage} isCurrent={/^\/usage\/*/.test(location.pathname)}>
          利用状況
        </Link>
        <Link onClick={navOption} isCurrent={/^\/option\/*/.test(location.pathname)}>
          各種申請
        </Link>
      </NavigationArea>
      <ButtonWrap>
        <Button type="primary" onClick={navOrderCreate} label="駐車場検索を依頼" height="44px" />
        <Button type="primary" label="各種申請を依頼" onClick={navOptionCreate} height="44px" />
      </ButtonWrap>
      <NotificationWrapper>
        <NotificationArea onClick={() => navigate("/news")} isCurrent={/^\/news\/*/.test(location.pathname)}>
          <FontAwesomeIcon
            icon={/^\/news\/*/.test(location.pathname) ? faBellFilled : faBellSolid}
            size="1x"
            color={color.text.gray}
          />
          <NotificationLabel>お知らせ</NotificationLabel>
          {unreadCount > 0 && <UnreadBadge>{unreadCount}</UnreadBadge>}
        </NotificationArea>
      </NotificationWrapper>
      <MenuList onClick={() => setIsOpened(!isOpened)}>
        <UserWrap isBlack={isOpened}>
          <UserIconWrap>
            <FontAwesomeIcon icon={faCircleUser} />
          </UserIconWrap>
          <UserName>{user.lastName + user.firstName}</UserName>
        </UserWrap>
        <AngleDownIcon icon={faAngleDown} size="sm" />
        <SubNavigationArea onMouseLeave={() => setIsOpened(!isOpened)} hidden={!isOpened}>
          <SubLinkOfContactId>
            <ContactIdBody>
              <ContactIdLabel>企業ID</ContactIdLabel>
              <IconWrap>
                <Popup
                  onClick={() => copyOnClipboard(user.accountId, setCopied(true))}
                  fontSize={font.size12}
                  right="-6px"
                  top="-32px"
                  text="コピーしました"
                >
                  <PopupHelper>
                    <FontAwesomeIcon icon={!copied ? faClipboard : faClipboardCheck} />
                    <IconLabel>コピー</IconLabel>
                  </PopupHelper>
                </Popup>
              </IconWrap>
            </ContactIdBody>
            <ContactIdWrap>{user.accountId}</ContactIdWrap>
          </SubLinkOfContactId>
          <SubLink onClick={navPassword}>
            <FontAwesomeIcon icon={faKey} />
            パスワード変更
          </SubLink>
          <SubLink onClick={navMfa}>
            <FontAwesomeIcon icon={faUserCircle} />
            2段階認証設定
          </SubLink>
          <SubLink onClick={navEmailSetting}>
            <FontAwesomeIcon icon={faEnvelope} />
            通知メール設定
          </SubLink>
          <SubLink onClick={logout}>
            <FontAwesomeIcon icon={faRightFromBracket} />
            ログアウト
          </SubLink>
        </SubNavigationArea>
      </MenuList>
    </Wrap>
  );
};

export default Header;
